import { useTheme } from '@mui/material/styles';
import useMediaQuery from '@mui/material/useMediaQuery';
import React from 'react';

import { COLORS } from './constants';

import { NHDFinding, NHDFindings } from '@/types/NHD';

export interface NHDHeroCardProps {
  /** The single dominant NHD finding to surface as a hero callout. Null renders nothing. */
  finding:
    | (NHDFinding & {
        /** Subfactor id this finding influenced — informs icon. */ subfactorId?: string;
      })
    | null;
  /** Optional explicit override for the icon variant. */
  icon?: 'fire' | 'water' | 'radon' | 'earthquake' | 'generic';
}

const FireIcon = (
  <svg
    width="100%"
    height="100%"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="1.5"
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden>
    <path
      d={
        'M8.5 14.5A2.5 2.5 0 0 0 11 12c0-1.38-.5-2-1-3-1.072-2.143-.224-4.054 2-6 ' +
        '.5 2.5 2 4.9 4 6.5 2 1.6 3 3.5 3 5.5a7 7 0 1 1-14 0c0-1.153.433-2.294 ' +
        '1-3a2.5 2.5 0 0 0 2.5 2.5z'
      }
    />
  </svg>
);

const WaterIcon = (
  <svg
    width="100%"
    height="100%"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="1.5"
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden>
    <path d="M12 2.69l5.66 5.66a8 8 0 1 1-11.31 0z" />
  </svg>
);

const EarthquakeIcon = (
  <svg
    width="100%"
    height="100%"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="1.5"
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden>
    <path d="M2 12 L5 12 L7 6 L9 18 L11 9 L13 15 L15 8 L17 14 L19 12 L22 12" />
  </svg>
);

const RadonIcon = (
  <svg
    width="100%"
    height="100%"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="1.5"
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden>
    <circle cx="12" cy="12" r="2" />
    <path d="M4 12a8 8 0 0 1 16 0" />
    <path d="M2 12a10 10 0 0 1 20 0" />
  </svg>
);

const GenericIcon = (
  <svg
    width="100%"
    height="100%"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="1.5"
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden>
    <path d="M12 2 L2 22 L22 22 Z" />
    <line x1="12" y1="10" x2="12" y2="14" />
    <circle cx="12" cy="18" r="0.6" fill="currentColor" />
  </svg>
);

const inferIcon = (subfactorId?: string): NHDHeroCardProps['icon'] => {
  if (!subfactorId) return 'generic';
  if (subfactorId.includes('fire')) return 'fire';
  if (subfactorId.includes('flood') || subfactorId.includes('water')) return 'water';
  if (subfactorId.includes('air') || subfactorId.includes('radon')) return 'radon';
  if (subfactorId.includes('seismic') || subfactorId.includes('soil')) return 'earthquake';
  return 'generic';
};

const ICON_MAP: Record<string, React.ReactElement> = {
  fire: FireIcon,
  water: WaterIcon,
  radon: RadonIcon,
  earthquake: EarthquakeIcon,
  generic: GenericIcon,
};

export type NHDHeroContentProps = NHDHeroCardProps;

/**
 * Icon + headline + implication note for the single most-impactful NHD
 * finding on a property — typically Very High FHSZ. No card chrome of its
 * own so it can be embedded as the always-visible highlight at the top of
 * another card (see NHDDisclosuresCard's `heroFinding` prop).
 *
 * Renders nothing when there's no finding to feature.
 */
export const NHDHeroContent = ({ finding, icon }: NHDHeroContentProps) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  if (!finding) return null;
  const variant = icon ?? inferIcon(finding.subfactorId);
  const iconNode = variant ? ICON_MAP[variant] ?? GenericIcon : GenericIcon;

  // Headline leads with severity for punch + brevity:
  // "Very High Fire Hazard" rather than "Fire Hazard Severity — Very High".
  // Strips the redundant "Severity" suffix when the zone already conveys it.
  const headline = finding.zone
    ? `${finding.zone} ${finding.label.replace(/\s*Severity$/i, '')}`
    : finding.label;

  return (
    <div
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: isMobile ? 14 : 22,
        color: COLORS.text,
      }}>
      <div
        style={{
          flexShrink: 0,
          color: COLORS.critical,
          width: isMobile ? 54 : 58,
          height: isMobile ? 54 : 58,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}>
        {iconNode}
      </div>
      <div style={{ flex: 1, minWidth: 0 }}>
        <div
          style={{
            fontSize: 17,
            fontWeight: 300,
            color: COLORS.text,
            lineHeight: 1.25,
            marginBottom: 6,
          }}>
          {headline}
        </div>
        {finding.detail && (
          <div
            style={{
              fontSize: 12,
              color: COLORS.textDim,
              lineHeight: 1.55,
            }}>
            {finding.detail}
          </div>
        )}
      </div>
    </div>
  );
};

/**
 * Standalone pink-toned hero card. No longer used on ContextScoreView
 * (folded into NHDDisclosuresCard so the highlight and disclosures read as
 * one section) but kept as a reusable wrapper around NHDHeroContent.
 */
export const NHDHeroCard = ({ finding, icon }: NHDHeroCardProps) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  if (!finding) return null;
  return (
    <div
      style={{
        background: 'var(--tx-glass-panel)',
        backgroundImage: 'var(--tx-surface-light)',
        boxShadow: 'var(--tx-shadow)',
        borderRadius: '24px',
        padding: isMobile ? '16px' : '20px 24px',
        border: 0,
      }}>
      <NHDHeroContent finding={finding} icon={icon} />
    </div>
  );
};

/**
 * Picks the single most-impactful finding from a NHDFindings payload to
 * surface in the hero. Priority: fire > seismic > flood > radon > other.
 * Returns null when no priority hit exists.
 */
export const pickHeroFinding = (
  findings: NHDFindings | null,
): (NHDFinding & { subfactorId?: string }) | null => {
  if (!findings) return null;
  const priority = ['fire_risk', 'seismic_risk', 'flood_risk', 'air_indoor_quality'];
  for (const key of priority) {
    const f = findings.influencingSubfactors?.[key];
    if (f && f.inZone) return { ...f, subfactorId: key };
  }
  // Fallback: first in-zone influencing subfactor, if any.
  const first = Object.entries(findings.influencingSubfactors ?? {}).find(([, v]) => v.inZone);
  return first ? { ...first[1], subfactorId: first[0] } : null;
};

export default NHDHeroCard;
